import React, { useState, useEffect } from 'react';
import ProductImage from '@nykaa/product-image-desktop';
import { useSelector } from 'react-redux';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import styled from 'App/styled';
import { pushData, pushEvent } from '@nykaa/data-layer/utils';
import {
  selectCurrentSku,
  selectProductMedia,
  selectProductRelatedVideo,
} from 'shared/store/product/selector';
import { State } from 'shared/store/types';
import WishlistIcon from '../Actions/Wishlist';
import { PageSource } from 'constants/dataLayer';
import { CUSTOM_WISHLIST_CLASS } from 'shared/constants/product';
import TryIt from '../TryItOn/Index';
import mirror from '../TryItOn/Images/mirror.png';
import {
  IMAGE_TYPE,
  PDP_IMAGE_POPUP_OPEN,
  PDP_VIDEO_POPUP_OPEN,
  VIDEO_TYPE,
} from 'components/CustomVideoPlayer/constants';
import MediaModal from '../../../components/MediaModal';

interface MediaProps {
  pdpCustomVideoEnabled: boolean;
  handleShowCAB: () => void;
  primaryCategories: any;
}

const Container = styled.div`
  position: relative;
  border-right: 1px solid ${({ theme }) => hexToRgb(theme.colors.state, 0.22)};
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  @media (min-width: 768px) and (max-width: 1024px) {
    flex: 1;
  }
  padding: 0 ${({ theme }) => theme.spacing.spacing80};
  .${CUSTOM_WISHLIST_CLASS} {
    .wishlist_button {
      display: none;
    }
  }
  .image-modal-wrapper {
    top: 0;
  }
`;
const TryItOnWrapper = styled.div`
  display: flex;
  align-self: center;
  margin-top: 70px;
  padding: 10px 20px;
  border: 1px solid #3f414d;
  color: #3f414d;
  cursor: pointer;
  align-items: center;
  border-radius: 3px;
  ${({ theme }) => theme.typography.buttonLarge};
`;

const ImageWrapper = styled.div`
  img {
    width: 20px;
    height: 20px;
    margin-right: 5px;
  }
`;
const EmptyImageWrapper = styled.div`
  width: 400px;
`;

const Media = ({
  pdpCustomVideoEnabled,
  handleShowCAB,
  primaryCategories,
}: MediaProps) => {
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const product = useSelector((state: State) => state.productPage.product);
  const TRY_IT_ON_TEXT = 'Try It On';
  const newMedia =
    useSelector((state: State) => selectProductMedia(state)) || [];
  const { tryItOn } = product || 0;
  const [openTryItOn, setOpenTryItOn] = useState(false);
  const numberOfMedia = newMedia?.length;
  const [modalState, setModalState] = useState(false);
  const [currentTab, setCurrentTab] = useState(IMAGE_TYPE);
  const videos = newMedia.filter((item) => item.mediaType === VIDEO_TYPE) || [];
  /* eslint-disable react-hooks/exhaustive-deps */
  const relatedVideos =
    useSelector((state: State) => selectProductRelatedVideo(state)) || [];
  const Videos = videos.concat(relatedVideos || []);
  const [selectedImage, setSelectedImage] = useState(newMedia[0]);
  const [selectedVideo, setSelectedVideo] = useState(Videos[0]);
  const productTrackingData = {
    product,
  };

  const toggleTryItOn = () => {
    setOpenTryItOn(!openTryItOn);
  };

  const getProductTypeForTryItOn = (productTitle: string) => {
    let regExpForNails = productTitle.match(/nail/i);
    let regExpForLips = productTitle.match(/lip/i);
    if (regExpForNails) {
      return 'Nails';
    }
    if (regExpForLips) {
      return 'Lips';
    }
    return '';
  };
  const onProductImageClick = (type: string) => {
    setModalState(true);
    setCurrentTab(type);
    const tabType = type === VIDEO_TYPE ? 'Video' : 'Image';
    const slidePos = newMedia.findIndex(
      (item) => item?.id === selectedVideo?.id
    );
    const event =
      type === VIDEO_TYPE ? PDP_VIDEO_POPUP_OPEN : PDP_IMAGE_POPUP_OPEN;
    pushEvent(event, {
      pdpVideoData: {
        productId: product?.id,
        videoId: selectedVideo?.id,
        pdpvideoPosition: `carousel:${tabType}:${slidePos + 1}`,
      },
    });
  };

  const setSelectedSlide = (currentSlide) => {
    if (currentSlide?.mediaType === VIDEO_TYPE) {
      setSelectedVideo(currentSlide);
    } else {
      setSelectedImage(currentSlide);
    }
  };
  const pdpVideoFlow = pdpCustomVideoEnabled && Videos?.length > 0;
  const customVideoAvailable = videos.length > 0;
  useEffect(() => {
    if (
      pdpCustomVideoEnabled &&
      customVideoAvailable &&
      pdpVideoFlow &&
      Videos.length > 0
    ) {
      window.datalayer.pdpVideoEnabled = `${customVideoAvailable}:${videos.length}:${relatedVideos.length}`;
      pushData({ pdpVideoCount: relatedVideos.length });
    } else {
      window.datalayer.pdpVideoEnabled = false;
    }
  }, [
    pdpCustomVideoEnabled,
    relatedVideos,
    customVideoAvailable,
    pdpVideoFlow,
    Videos.length,
    videos.length,
  ]);

  return (
    <Container>
      {openTryItOn ? (
        <TryIt
          productType={getProductTypeForTryItOn(currentSku?.title)}
          closeTryItOn={toggleTryItOn}
          productIcon={currentSku?.shadeImage}
        />
      ) : (
        <>
          <WishlistIcon
            pageLocation={PageSource.Product}
            customWishlistClass={CUSTOM_WISHLIST_CLASS}
            productTrackingData={productTrackingData}
          />
          {numberOfMedia > 0 ? (
            <ProductImage
              images={newMedia}
              visibleSlides={5}
              ImageClickCallback={(type) => onProductImageClick(type)}
              selectedSlideCallback={(selectedSlide) =>
                setSelectedSlide(selectedSlide)
              }
              pdpCustomVideoEnabled={pdpVideoFlow}
            />
          ) : (
            <EmptyImageWrapper />
          )}
        </>
      )}

      {modalState && (
        <MediaModal
          setModalState={setModalState}
          handleShowCAB={handleShowCAB}
          currentTabView={currentTab}
          primaryCategories={primaryCategories}
          currentImage={selectedImage}
          currentVideo={selectedVideo}
        />
      )}
      {!!tryItOn && (
        <TryItOnWrapper onClick={toggleTryItOn}>
          <ImageWrapper>
            <img
              src={mirror}
              alt="mirror"
            />
          </ImageWrapper>
          {TRY_IT_ON_TEXT}
        </TryItOnWrapper>
      )}
    </Container>
  );
};
export default Media;
